import { base, brand, system } from '@workday/canvas-tokens-web'
// eslint-disable-next-line
import { extendTheme } from '@chakra-ui/react'
import { omitBy } from 'lodash/fp'
import { textStyles } from './text-styles'
import { styles } from './styles'
import { layerStyles, legacyCardLayerStyles } from './layer-styles'
import components from './components'
import legacyVndlyColors from './colors'

const shades = new RegExp(
  `^(${[
    'amber',
    'azure',
    'blue',
    'coral',
    'green',
    'indigo',
    'magenta',
    'neutral',
    'orange',
    'purple',
    'red',
    'slate',
    'teal'
  ].join('|')})[0-9]{2,3}$`
)

const omitDeprecatedTokens = omitBy((value, key) => !shades.test(key))

const baseVars = wrapCssVars(omitDeprecatedTokens(base))
const brandVars = wrapCssVars(brand)
const systemVars = wrapCssVars(system.color)
const shapeVars = wrapCssVars(system.shape)
const depthVars = wrapCssVars(system.depth)

const appShadowTokens = {
  base: '0 1px 2px 0 rgba(0, 0, 0, 0.1), 0 1px 1px 0 rgba(0, 0, 0, 0.06)',
  // The default "focus" ring of all chakra elements uses this outline boxShadow
  // Both rings are rendered outside the element bounds giving a fuller outer ring
  outline: '0px 0px 0px 2px white, 0px 0px 0px 4px var(--vndly-color-container-border-focused)',
  // This outline has the white ring inside and the focused ring outside giving a partially inner and partially outer ring
  middleOutline: 'inset 0px 0px 0px 2px white, 0px 0px 0px 2px var(--vndly-color-container-border-focused)',
  // This outline has both rings inside the element giving a fully inset ring
  insetOutline: 'inset 0px 0px 0px 2px var(--vndly-color-container-border-focused), inset 0px 0px 0px 4px white'
}

/** Shared Chakra theme without Canvas v4 system.shape / system.depth on radii and numeric depth shadows. */
const workdayThemeConfig = {
  styles,
  components,
  breakpoints: {
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1500px',
    // Why is there an extra breakpoint that is the same as xl??
    // To fix a bug that caused pages to freeze completely and crash:
    // https://github.com/chakra-ui/chakra-ui/issues/4301
    '2xl': '1500px' // 1440px
  },
  colors: {
    ...legacyVndlyColors,
    // Spreads in the workday tokens by category: base, system and brand
    // See: https://canvas.workdaydesign.com/styles/tokens/color
    base: baseVars,
    system: { color: systemVars },
    brand: brandVars
  },
  shadows: appShadowTokens,

  fonts: {
    body: '"Roboto", "Helvetica Neue", "Helvetica", Arial, sans-serif',
    heading: '"Roboto", "Helvetica Neue", "Helvetica", Arial, sans-serif',
    mono: '"Roboto Mono", "Courier New", Courier, monospace'
  },
  fontWeights: {
    light: 300,
    normal: 400,
    semibold: 500,
    bold: 700
  },
  space: {
    sm: '0.5rem',
    md: '1rem',
    lg: '1.5rem',
    xl: '2rem'
  },
  layerStyles,
  textStyles
}

/**
 * Theme used when `sana_rebrand_enabled` is off or still loading — Chakra default radii plus app shadows only
 * (no Canvas shape/depth token mapping on borderRadius / boxShadow numeric slots).
 */
export const workdayThemeLegacyVisualTokens = extendTheme(workdayThemeConfig, {
  // Lets baseStyle / variants branch without temporary CSS variables.
  isSanaRebrand: false,
  radii: { xxl: `var(${system.shape.xxl})` },
  layerStyles: {
    ...layerStyles,
    ...legacyCardLayerStyles
  }
})

/**
 * Canvas v4 type-ramp alignment ("Mapping B", VNDLY-62491 / E01), applied ONLY in
 * the flag-ON theme via the `workdayThemeSanaRebrand` `extendTheme` call.
 *
 * STANDALONE source of truth: every style declares all of its own properties (type,
 * color, transform, decoration) and relies on NO inheritance from the shared legacy
 * `textStyles`. When `sana_rebrand_enabled` is eventually removed, this object can
 * become the sole `textStyles` with nothing lost in the deep merge.
 *
 * Each style is the closest **non-deprecated** Canvas composite type token
 * (`system.type.<role>.<size>`) plus explicit overrides for what the composite doesn't
 * carry (weight, color, uppercase, tooltip affordance). `wrapCssVars` wraps the whole
 * composed object once, so the helpers below hold raw Canvas token names.
 *
 * Letter-spacing comes straight from the Canvas composites (E01 adopts Canvas tracking
 * for body / subtext text). The only tracking override is `widest` on the all-caps
 * section labels — uppercase tracking isn't modelled by the Canvas type tokens.
 *
 * The composites resolve to Roboto (`--cnvs-sys-font-family-default`), so the font
 * family is unchanged — E01 keeps Roboto; the v5 font swap is E02.
 *
 * Override groups (raw Canvas tokens; wrapped by each style's `wrapCssVars`):
 * - `semibold` — Canvas body/subtext composites default to weight `normal`; VNDLY keeps
 *   its semibold (Canvas `fontWeight.medium` = 500). Headings need none — already bold.
 * - `uppercaseLabel` — section labels are uppercase with `widest` tracking.
 * - `hintColor` / `linkColor` / `criticalColor` — Canvas semantic text colors
 *   (`system.color.fg.muted.default` / `.brand.fg.primary.default` / `.brand.fg.critical.default`).
 */
const semibold = { fontWeight: system.fontWeight.medium }
const uppercaseLabel = { textTransform: 'uppercase', letterSpacing: 'widest' }
// The global default text color. The body ramp carries it so a text style enforces its
// own color — consumers set `color` only to *override* the default, never to complete it.
const defaultColor = { color: system.color.fg.default }
const hintColor = { color: system.color.fg.muted.default }
const linkColor = { color: system.color.brand.fg.primary.default }
const criticalColor = { color: system.color.brand.fg.critical.default }

const sanaRebrandTextStyles = {
  // Headings — composite verbatim (Canvas heading ramp is bold by default).
  heading1: wrapCssVars(system.type.heading.lg),
  heading2: wrapCssVars(system.type.heading.md),
  heading3: wrapCssVars(system.type.heading.sm),

  // Large body (former heading3) — composite verbatim, normal weight.
  bodyLarge: wrapCssVars({ ...system.type.body.lg, ...defaultColor }),

  // Card titles: body-lg, bold, fg-strong.
  heading4: wrapCssVars({ ...system.type.body.lg, fontWeight: system.fontWeight.bold, color: system.color.fg.strong }),

  // Uppercase section labels — VNDLY size, semibold, uppercase + `widest` tracking.
  sectionHeading: wrapCssVars({ ...system.type.body.md, ...semibold, ...uppercaseLabel }),
  sectionSubheading: wrapCssVars({ ...system.type.body.sm, ...semibold, ...uppercaseLabel, ...hintColor }),

  // 16px text — body.sm composite (Canvas tracking retained).
  body: wrapCssVars({ ...system.type.body.sm, ...defaultColor }),
  bodySemi: wrapCssVars({ ...system.type.body.sm, ...semibold, ...defaultColor }),
  subtext: wrapCssVars({ ...system.type.body.sm, ...hintColor }),
  subtextSemi: wrapCssVars({ ...system.type.body.sm, ...semibold, ...hintColor }),
  link: wrapCssVars({ ...system.type.body.sm, ...semibold, ...linkColor }),
  error: wrapCssVars({ ...system.type.body.sm, ...semibold, ...criticalColor }),
  tooltip: wrapCssVars({
    ...system.type.body.sm,
    ...semibold,
    ...linkColor,
    textDecoration: 'underline dashed 1px',
    cursor: 'help'
  }),

  // 14px text — subtext.lg composite (Canvas tracking retained).
  bodySmall: wrapCssVars({ ...system.type.subtext.lg, ...defaultColor }),
  subtextSmall: wrapCssVars({ ...system.type.subtext.lg, ...hintColor }),
  bodySmallSemi: wrapCssVars({ ...system.type.subtext.lg, ...semibold, ...defaultColor }),
  subtextSmallSemi: wrapCssVars({ ...system.type.subtext.lg, ...semibold, ...hintColor }),
  linkSmall: wrapCssVars({ ...system.type.subtext.lg, ...semibold, ...linkColor }),
  errorSmall: wrapCssVars({ ...system.type.subtext.lg, ...semibold, ...criticalColor })
}

/**
 * Full Sana / Canvas v4 shape + depth integration for Chakra `radii` and `shadows`,
 * plus the Canvas v4 type-ramp `textStyles` (Mapping B).
 */
export const workdayThemeSanaRebrand = extendTheme(workdayThemeConfig, {
  // Explicit rebrand marker. Emotion-styled components (ReactSelect, VndlySelect) can't
  // read the feature flag directly from a Chakra theme fn, so they read this instead of
  // inferring the rebrand from the presence of Canvas radii keys.
  isSanaRebrand: true,
  radii: shapeVars,
  shadows: {
    ...depthVars,
    ...appShadowTokens
  },
  textStyles: sanaRebrandTextStyles
})

/** @deprecated Prefer `workdayThemeLegacyVisualTokens` or `workdayThemeSanaRebrand`; this is the legacy visual set. */
export const workdayTheme = workdayThemeLegacyVisualTokens

/**
 * @deprecated
 * These are our old VNDLY custom text styles. Instead of using these text styles we will be utilizing
 * the Chakra text styles listed above. Please do not use these styles any more.
 * */
export { default as textStyles } from './legacy_text_styles'

/**
 * Recursive function to transform any --css-var-named-values to be wrapped with `var()`:
 * e.g. '--cnvs-something' becomes 'var(--cnvs-something)'
 */
export function wrapCssVars(obj: any): any {
  if (typeof obj === 'string') {
    if (obj.startsWith('--cnvs')) {
      return `var(${obj})`
    }
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(wrapCssVars)
  }

  if (obj !== null && typeof obj === 'object') {
    const result: any = {}
    // Use Object.keys to get enumerable properties, or Object.getOwnPropertyNames for all properties
    for (const key of Object.keys(obj)) {
      result[key] = wrapCssVars(obj[key])
    }
    return result
  }

  return obj
}
